import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalIcon, Clock, MapPin, ArrowRight, CheckCircle2, ArrowLeft, Loader2, Car } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDay = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

/** Small horizontal date-strip picker — next 14 days, weekends dimmed if closed */
function DateStrip({ value, onChange }) {
  const days = useMemo(() => {
    const out = [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i++) {
      const d = new Date(today); d.setDate(today.getDate() + i);
      out.push(d);
    }
    return out;
  }, []);
  return (
    <div className="flex gap-2 overflow-x-auto pb-2" data-testid="schedule-date-strip">
      {days.map((d) => {
        const key = iso(d);
        const active = key === value;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            data-testid={`schedule-date-${key}`}
            className={`flex-shrink-0 w-24 md:w-28 p-3 border-2 text-left transition-colors ${
              active ? 'border-[#E63946] bg-[#E63946]/[0.08]' : 'mi-line-strong bg-[#0A0A0A] hover:border-white/30'
            }`}
          >
            <div className={`mi-eyebrow text-[10px] ${active ? 'text-[#E63946]' : 'text-white/50'}`}>
              {d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
            </div>
            <div className="mi-headline text-2xl leading-tight mt-1">{d.getDate()}</div>
            <div className="text-white/50 text-[10px] uppercase">{d.toLocaleDateString('en-US', { month: 'short' })}</div>
          </button>
        );
      })}
    </div>
  );
}

export default function SchedulePage({ embed = false }) {
  const [settings, setSettings] = useState(null);
  const [date, setDate] = useState(() => iso(new Date()));
  const [slots, setSlots] = useState([]);
  const [slotBusy, setSlotBusy] = useState(false);
  const [picked, setPicked] = useState(null);        // slot object
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    service_type: '', vehicle_year: '', vehicle_make: '', vehicle_model: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // Capture UTM params on mount so we know which channel drove the booking.
  const utm = useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const src = q.get('utm_source'); const med = q.get('utm_medium'); const camp = q.get('utm_campaign');
      return (src || med || camp) ? { utm_source: src, utm_medium: med, utm_campaign: camp } : {};
    } catch { return {}; }
  }, []);

  useEffect(() => {
    api.get('/schedule/settings/public')
      .then(({ data }) => { setSettings(data); setForm((f) => ({ ...f, service_type: data.service_types[0] })); })
      .catch(() => toast.error('Could not load scheduler settings'));
  }, []);

  useEffect(() => {
    if (!date) return;
    setSlotBusy(true); setPicked(null);
    api.get('/schedule/availability', { params: { date } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => toast.error('Could not load available times'))
      .finally(() => setSlotBusy(false));
  }, [date]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!picked) return toast.error('Pick a time slot first');
    if (!form.name || !form.email || !form.phone) return toast.error('Name, email and phone are required');
    setBusy(true);
    try {
      const { data } = await api.post('/schedule/book', {
        ...form, ...utm, start_iso: picked.start_iso,
      });
      setConfirmed({ ...data, slot: picked, form });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not book — the slot may have just been taken.');
    } finally {
      setBusy(false);
    }
  };

  if (confirmed) {
    return (
      <div className="relative min-h-screen bg-black">
        {!embed && <Header />}
        <main className={embed ? 'max-w-3xl mx-auto px-4 py-8' : 'max-w-3xl mx-auto px-6 py-24'}>
          <div className="border-2 border-[#E63946] bg-[#E63946]/[0.04] p-6 md:p-10" data-testid="schedule-confirmation">
            <CheckCircle2 className="w-10 h-10 text-[#E63946] mb-3" />
            <div className="mi-eyebrow text-[#E63946] mb-2">// BOOKING CONFIRMED</div>
            <h1 className={`mi-headline ${embed ? 'text-3xl' : 'text-4xl md:text-5xl'} mb-3`}>You're on the calendar.</h1>
            <p className="text-white/70 leading-relaxed mb-6 text-sm">
              We just sent a confirmation (with a .ics calendar invite) to <span className="text-white font-mono">{confirmed.form.email}</span>{' '}
              and our team at <span className="text-white font-mono">sales@motorinks.com</span>. Open the .ics to auto-add it to your calendar.
            </p>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <div className="p-4 border mi-line-strong bg-black/60">
                <div className="mi-eyebrow text-[10px] text-white/50 mb-1">WHEN</div>
                <div className="mi-headline text-xl">{fmtDay(new Date(confirmed.slot.start_iso))}</div>
                <div className="text-white/70 text-sm">{confirmed.slot.label}</div>
              </div>
              <div className="p-4 border mi-line-strong bg-black/60">
                <div className="mi-eyebrow text-[10px] text-white/50 mb-1">SERVICE</div>
                <div className="mi-headline text-xl">{confirmed.form.service_type}</div>
                <div className="text-white/70 text-sm">
                  {[confirmed.form.vehicle_year, confirmed.form.vehicle_make, confirmed.form.vehicle_model].filter(Boolean).join(' ') || '—'}
                </div>
              </div>
            </div>
            {!embed && <Link to="/" className="mi-btn-ghost text-xs"><ArrowLeft className="w-3 h-3" /> Back to home</Link>}
          </div>
        </main>
        {!embed && <Footer />}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black">
      {!embed && <Header />}
      <main className={embed ? 'max-w-3xl mx-auto px-4 py-6' : 'max-w-5xl mx-auto px-6 py-12 md:py-20'}>
        {!embed && <Link to="/" className="mi-btn-ghost text-xs mb-6"><ArrowLeft className="w-3 h-3" /> Back</Link>}

        <div className="mi-eyebrow text-[#E63946] mb-2">// BOOK YOUR APPOINTMENT</div>
        <h1 className={`mi-headline ${embed ? 'text-3xl md:text-4xl' : 'text-5xl md:text-6xl'} mb-3`}>
          {embed ? 'Book with Motor iNKS.' : 'Book direct with Motor iNKS.'}
        </h1>
        <p className={`text-white/60 max-w-2xl ${embed ? 'mb-6 text-sm' : 'mb-10 leading-relaxed'}`}>
          Pick a day, pick a slot, drop your details — we'll confirm within 24 hours.
        </p>

        {/* Step 1 — date */}
        <div className="mb-8">
          <div className="mi-eyebrow text-white/50 mb-3 flex items-center gap-2"><CalIcon className="w-3.5 h-3.5" /> // 1 · PICK A DAY</div>
          <DateStrip value={date} onChange={setDate} />
        </div>

        {/* Step 2 — slot */}
        <div className="mb-8">
          <div className="mi-eyebrow text-white/50 mb-3 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> // 2 · PICK A TIME</div>
          {slotBusy ? (
            <div className="text-white/50 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking availability…</div>
          ) : slots.length === 0 ? (
            <div className="p-6 border mi-line bg-black/40 text-white/60 text-sm">
              No availability on this day — try a weekday.
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="schedule-slot-grid">
              {slots.map((s) => (
                <button
                  key={s.start_iso}
                  onClick={() => s.available && setPicked(s)}
                  disabled={!s.available}
                  data-testid={`schedule-slot-${s.start_iso}`}
                  className={`p-3 border-2 text-center transition-all ${
                    picked?.start_iso === s.start_iso
                      ? 'border-[#E63946] bg-[#E63946] text-white'
                      : s.available
                        ? 'mi-line-strong bg-[#0A0A0A] hover:border-[#E63946]/60'
                        : 'mi-line bg-black/40 text-white/25 line-through cursor-not-allowed'
                  }`}
                >
                  <div className="mi-headline text-base">{s.label}</div>
                  {!s.available && <div className="text-[9px] mi-eyebrow opacity-70">TAKEN</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Step 3 — details */}
        <form onSubmit={submit} className="border mi-line-strong bg-[#0A0A0A] p-6 md:p-8">
          <div className="mi-eyebrow text-white/50 mb-4 flex items-center gap-2"><Car className="w-3.5 h-3.5" /> // 3 · YOUR DETAILS</div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Field label="Full name *"><input required value={form.name} onChange={upd('name')} data-testid="schedule-name" className="mi-input" /></Field>
            <Field label="Email *"><input required type="email" value={form.email} onChange={upd('email')} data-testid="schedule-email" className="mi-input" /></Field>
            <Field label="Phone *"><input required value={form.phone} onChange={upd('phone')} data-testid="schedule-phone" className="mi-input" placeholder="832-555-0101" /></Field>
            <Field label="Service">
              <select value={form.service_type} onChange={upd('service_type')} data-testid="schedule-service" className="mi-input">
                {settings?.service_types?.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <Field label="Year"><input value={form.vehicle_year} onChange={upd('vehicle_year')} className="mi-input" placeholder="2023" /></Field>
            <Field label="Make"><input value={form.vehicle_make} onChange={upd('vehicle_make')} className="mi-input" placeholder="BMW" /></Field>
            <Field label="Model"><input value={form.vehicle_model} onChange={upd('vehicle_model')} className="mi-input" placeholder="M3" /></Field>
          </div>
          <Field label="Notes (optional)">
            <textarea rows={3} value={form.notes} onChange={upd('notes')} data-testid="schedule-notes" className="mi-input" placeholder="Colors, coverage, timeline, anything we should know…" />
          </Field>

          {picked && (
            <div className="mt-4 p-4 border-l-4 border-[#E63946] bg-[#E63946]/[0.06] text-sm">
              <div className="mi-eyebrow text-[#E63946] text-[10px] mb-1">// SLOT LOCKED</div>
              <div className="mi-headline text-lg">
                {fmtDay(new Date(picked.start_iso))} · {picked.label}
              </div>
              <div className="text-white/60 text-xs mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {settings?.location}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !picked}
            data-testid="schedule-book-btn"
            className="mi-btn-red mt-6 w-full md:w-auto justify-center"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {picked ? 'Book this appointment' : 'Pick a slot to continue'}
          </button>
          <div className="mt-3 text-white/40 text-[11px]">
            No card required. We'll email you a confirmation and a calendar invite.
          </div>
        </form>
      </main>
      {!embed && <Footer />}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mi-eyebrow text-white/50 text-[10px] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
